// NotificationTemplatesView.jsx - Fixed version
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import DataTable from "../ui/DataTable";
import NotificationsService from '../../services/NotificationsService';
import SimpleRichTextEditor from '../ui/SimpleRichTextEditor';
import 'react-confirm-alert/src/react-confirm-alert.css';

// Reusable Form Components - Moved to top level
const FormInput = ({ label, error, fullWidth = false, required = false, ...props }) => (
    <div className={`form-group ${fullWidth ? 'full-width' : ''}`}>
        <label className="form-label">
            {label} {required && <span className="required">*</span>}
        </label>
        <input
            className={`form-input ${error ? 'error' : ''}`}
            {...props}
        />
        {error && <span className="error-message">{error}</span>}
    </div>
);

const FormSelect = ({ label, error, children, required = false, ...props }) => (
    <div className="form-group">
        <label className="form-label">
            {label} {required && <span className="required">*</span>}
        </label>
        <div className="select-wrapper">
            <select
                className={`form-select ${error ? 'error' : ''}`}
                {...props}
            >
                {children}
            </select>
            <div className="select-arrow">▼</div>
        </div>
        {error && <span className="error-message">{error}</span>}
    </div>
);

// Subcomponent: ContentTab
const ContentTab = ({
                        formData,
                        errors,
                        handleInputChange,
                        handleRichTextChange,
                        handleTextareaChange,
                        readOnly,
                        showEmailEditor,
                        showSmsEditor,
                        notificationEnums,
                        reservedWords
                    }) => (
    <>
        {/* Basic Information */}
        <div className="form-section">
            <div className="section-header">
                <h3 className="section-title">Basic Information</h3>
                <div className="section-divider"></div>
            </div>
            <div className="form-grid">
                <FormSelect
                    label="Platform"
                    name="notificationPlatform"
                    value={formData.notificationPlatform}
                    onChange={handleInputChange}
                    error={errors.notificationPlatform}
                    disabled={readOnly}
                    required
                >
                    <option value="">Select Platform</option>
                    <option value="EMAIL">Email</option>
                    <option value="SMS">SMS</option>
                    <option value="BOTH">Both Email & SMS</option>
                </FormSelect>

                <FormSelect
                    label="Type"
                    name="notificationType"
                    value={formData.notificationType}
                    onChange={handleInputChange}
                    error={errors.notificationType}
                    disabled={readOnly}
                    required
                >
                    <option value="">Select Type</option>
                    {notificationEnums?.notificationTypes?.map(type => (
                        <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                </FormSelect>

                <FormInput
                    label="Subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleInputChange}
                    error={errors.subject}
                    disabled={readOnly}
                    placeholder="Enter notification subject"
                    required
                    fullWidth
                />
            </div>
        </div>

        {/* Email Content */}
        {showEmailEditor && (
            <div className="form-section">
                <div className="section-header">
                    <h3 className="section-title">
                        Email Content <span className="required">*</span>
                    </h3>
                    <p className="section-subtitle">
                        Use variables like {'{{variable}}'} to personalize content
                    </p>
                </div>
                <SimpleRichTextEditor
                    value={formData.message}
                    onChange={handleRichTextChange}
                    readOnly={readOnly}
                    height="400px"
                    reservedWords={reservedWords}
                />
                {errors.message && (
                    <span className="error-message">{errors.message}</span>
                )}
            </div>
        )}

        {/* SMS Content */}
        {showSmsEditor && (
            <div className="form-section">
                <div className="section-header">
                    <h3 className="section-title">
                        SMS Content <span className="required">*</span>
                    </h3>
                    <p className="section-subtitle">
                        Maximum 200 characters
                    </p>
                </div>
                <div className="sms-editor-container">
          <textarea
              value={formData.sms}
              name="sms"
              onChange={handleTextareaChange}
              disabled={readOnly}
              placeholder="Enter SMS message..."
              maxLength="200"
              className={`sms-textarea ${errors.sms ? 'error' : ''}`}
          />
                    <div className="sms-counter">
            <span className={`char-count ${formData.sms?.length > 180 ? 'warning' : ''}`}>
              {formData.sms?.length || 0}
            </span>
                        <span className="char-total">/200</span>
                    </div>
                </div>
                {errors.sms && (
                    <span className="error-message">{errors.sms}</span>
                )}
            </div>
        )}
    </>
);

// Subcomponent: EmailSettingsTab
const EmailSettingsTab = ({
                              formData,
                              errors,
                              handleInputChange,
                              readOnly,
                              smtpSetups
                          }) => (
    <div className="form-section">
        <div className="section-header">
            <h3 className="section-title">Email Configuration</h3>
            <p className="section-subtitle">
                Configure SMTP and email recipients
            </p>
        </div>
        <div className="form-grid">
            <FormSelect
                label="SMTP Setup"
                name="smtpId"
                value={formData.smtpId || ''}
                onChange={handleInputChange}
                error={errors.smtpId}
                disabled={readOnly}
                required
            >
                <option value="">Select SMTP Setup</option>
                {(smtpSetups || []).map(setup => (
                    <option key={setup.id} value={setup.id}>
                        {setup.setUpName}
                    </option>
                ))}
            </FormSelect>
        </div>
    </div>
);

// Subcomponent: TemplateModal
const TemplateModal = ({
                           modalTitle,
                           formData,
                           errors,
                           activeTab,
                           setActiveTab,
                           handleInputChange,
                           handleRichTextChange,
                           handleTextareaChange,
                           handleCheckboxChange,
                           handleCloseModal,
                           handleSave,
                           saving,
                           readOnly,
                           showEmailFields,
                           showEmailEditor,
                           showSmsEditor,
                           notificationEnums,
                           smtpSetups,
                           reservedWords
                       }) => {
    const isViewMode = modalTitle === 'View Notification Template';

    return (
        <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="modal-container" onClick={e => e.stopPropagation()}>
                {/* Modal Header */}
                <div className="modal-header">
                    <div className="modal-title-section">
                        <h2 className="modal-title">{modalTitle}</h2>
                        {formData.id && (
                            <span className="template-id">ID: {formData.id}</span>
                        )}
                    </div>
                    <button onClick={handleCloseModal} className="close-modal-btn">
                        <span>×</span>
                    </button>
                </div>

                {/* Modal Tabs */}
                <div className="modal-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
                        onClick={() => setActiveTab('content')}
                    >
                        <span className="tab-icon">📝</span>
                        Content
                    </button>
                    {showEmailFields && (
                        <button
                            className={`tab-btn ${activeTab === 'email' ? 'active' : ''}`}
                            onClick={() => setActiveTab('email')}
                        >
                            <span className="tab-icon">✉️</span>
                            Email Settings
                        </button>
                    )}
                </div>

                {/* Modal Content */}
                <div className="modal-content">
                    {activeTab === 'content' ? (
                        <ContentTab
                            formData={formData}
                            errors={errors}
                            handleInputChange={handleInputChange}
                            handleRichTextChange={handleRichTextChange}
                            handleTextareaChange={handleTextareaChange}
                            readOnly={isViewMode}
                            showEmailEditor={showEmailEditor}
                            showSmsEditor={showSmsEditor}
                            notificationEnums={notificationEnums}
                            reservedWords={reservedWords}
                        />
                    ) : (
                        <EmailSettingsTab
                            formData={formData}
                            errors={errors}
                            handleInputChange={handleInputChange}
                            readOnly={isViewMode}
                            smtpSetups={smtpSetups}
                        />
                    )}
                </div>

                {/* Modal Footer */}
                <div className="modal-footer">
                    <div className="status-toggle">
                        <label className="toggle-label">
                            <input
                                type="checkbox"
                                checked={formData.activeNotification === 'YES'}
                                onChange={handleCheckboxChange}
                                disabled={isViewMode}
                                className="toggle-input"
                            />
                            <span className="toggle-slider"></span>
                            <span className="toggle-text">
                {formData.activeNotification === 'YES' ? 'Active' : 'Inactive'}
              </span>
                        </label>
                    </div>

                    {!isViewMode && (
                        <div className="footer-actions">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="save-btn"
                            >
                                {saving ? (
                                    <>
                                        <span className="spinner"></span>
                                        Saving...
                                    </>
                                ) : (
                                    'Save Template'
                                )}
                            </button>
                            <button
                                onClick={handleCloseModal}
                                disabled={saving}
                                className="cancel-btn"
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Main Component
const NotificationTemplatesView = ({ notificationEnums, smtpSetups, loading: externalLoading }) => {
    // State Management
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState(getInitialFormData());
    const [errors, setErrors] = useState({});
    const [modalTitle, setModalTitle] = useState('New Notification Template');
    const [searchVariable, setSearchVariable] = useState('');
    const [reservedWords, setReservedWords] = useState([]);
    const [activeTab, setActiveTab] = useState('content');

    // Initialization
    useEffect(() => {
        if (!externalLoading) {
            loadTemplates();
            loadReservedWords();
        }
    }, [externalLoading]);

    // Helper Functions
    function getInitialFormData() {
        return {
            id: null,
            notificationPlatform: 'EMAIL',
            notificationType: '',
            subject: '',
            message: '',
            sms: '',
            schemeId: null,
            templateApplication: '',
            activeNotification: 'YES',
            smtpId: null,
            primaryEmails: '',
            ccEmails: '',
            bccEmails: ''
        };
    }

    const getEnumLabel = (enumList, value) => {
        if (!enumList || !value) return '';
        const item = enumList.find(item => item.id === value);
        return item ? item.name : value;
    };

    // Data Loading
    const loadTemplates = async () => {
        try {
            setLoading(true);
            const response = await NotificationsService.getTemplates();
            setTemplates(response.success ? (response.data || []) : []);
        } catch (error) {
            console.error('Error loading templates:', error);
            setTemplates([]);
        } finally {
            setLoading(false);
        }
    };

    const loadReservedWords = async () => {
        try {
            const response = await NotificationsService.getReservedWords();
            if (response.success) {
                setReservedWords(response.data || []);
            }
        } catch (error) {
            console.error('Error loading reserved words:', error);
        }
    };

    // Validation
    const validateForm = () => {
        const newErrors = {};

        if (!formData.notificationPlatform) {
            newErrors.notificationPlatform = 'Notification platform is required';
        }

        if (!formData.notificationType) {
            newErrors.notificationType = 'Notification type is required';
        }

        if (!formData.subject?.trim()) {
            newErrors.subject = 'Subject is required';
        }

        if (formData.notificationPlatform === 'EMAIL' && !formData.message?.trim()) {
            newErrors.message = 'Email content is required';
        }

        if (formData.notificationPlatform === 'SMS' && !formData.sms?.trim()) {
            newErrors.sms = 'SMS content is required';
        }

        if (formData.notificationPlatform === 'EMAIL' && !formData.smtpId) {
            newErrors.smtpId = 'SMTP setup is required for email';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Form Handlers
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleRichTextChange = (content) => {
        setFormData(prev => ({ ...prev, message: content }));
        if (errors.message) {
            setErrors(prev => ({ ...prev, message: '' }));
        }
    };

    const handleTextareaChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleCheckboxChange = () => {
        setFormData(prev => ({
            ...prev,
            activeNotification: prev.activeNotification === 'YES' ? 'NO' : 'YES'
        }));
    };

    // Template Actions
    const handleEdit = (template) => {
        setModalTitle('Edit Notification Template');
        setFormData({
            id: template.id,
            notificationPlatform: template.notificationPlatform || 'EMAIL',
            notificationType: template.notificationType || '',
            subject: template.subject || '',
            message: template.message || '',
            sms: template.sms || '',
            schemeId: template.schemeId || null,
            templateApplication: template.templateApplication || '',
            activeNotification: template.activeNotification || 'YES',
            smtpId: template.smtpId || null,
            primaryEmails: template.primaryEmails || '',
            ccEmails: template.ccEmails || '',
            bccEmails: template.bccEmails || ''
        });
        setShowModal(true);
        setErrors({});
        setSearchVariable('');
        setActiveTab('content');
    };

    const handleView = async (template) => {
        try {
            const response = await NotificationsService.getTemplateById({ ids: [template.id] });
            if (response.success) {
                setModalTitle('View Notification Template');
                setFormData({
                    id: response.data.id,
                    notificationPlatform: response.data.notificationPlatform || '',
                    notificationType: response.data.notificationType || '',
                    subject: response.data.subject || '',
                    message: response.data.message || '',
                    sms: response.data.sms || '',
                    schemeId: response.data.schemeId || null,
                    templateApplication: response.data.templateApplication || '',
                    activeNotification: response.data.activeNotification || 'YES',
                    smtpId: response.data.smtpId || null,
                    primaryEmails: response.data.primaryEmails || '',
                    ccEmails: response.data.ccEmails || '',
                    bccEmails: response.data.bccEmails || ''
                });
                setShowModal(true);
                setErrors({});
                setSearchVariable('');
                setActiveTab('content');
            }
        } catch (error) {
            console.error('Error viewing template:', error);
        }
    };

    const handleDelete = useCallback((template) => {
        confirmAlert({
            title: 'Confirm Delete',
            message: `Are you sure you want to delete "${template.subject}"?`,
            buttons: [
                {
                    label: 'Yes, Delete',
                    onClick: async () => {
                        try {
                            setDeleting(true);
                            const response = await NotificationsService.deleteTemplate({ ids: [template.id] });
                            if (response.success) {
                                await loadTemplates();
                            }
                        } catch (error) {
                            console.error('Error deleting template:', error);
                        } finally {
                            setDeleting(false);
                        }
                    }
                },
                { label: 'Cancel', onClick: () => {} }
            ]
        });
    }, []);

    const handleSave = async () => {
        if (!validateForm()) return;

        try {
            setSaving(true);
            const response = await NotificationsService.saveTemplate(formData);

            if (response.success) {
                await loadTemplates();
                handleCloseModal();
            } else {
                alert('Failed to save template: ' + response.message);
            }
        } catch (error) {
            console.error('Error saving template:', error);
            alert('Error saving template: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData(getInitialFormData());
        setErrors({});
        setSearchVariable('');
        setActiveTab('content');
    };

    const handleNewTemplate = () => {
        setModalTitle('New Notification Template');
        setFormData(getInitialFormData());
        setShowModal(true);
        setErrors({});
        setSearchVariable('');
        setActiveTab('content');
    };

    // Computed Values
    const showEmailFields = formData.notificationPlatform === 'EMAIL' || formData.notificationPlatform === 'BOTH';
    const showEmailEditor = formData.notificationPlatform === 'EMAIL' || formData.notificationPlatform === 'BOTH';
    const showSmsEditor = formData.notificationPlatform === 'SMS' || formData.notificationPlatform === 'BOTH';

    // DataTable Columns
    const columns = useMemo(() => [
        {
            header: 'Subject',
            accessorFn: (row) => row.subject,
            id: 'subject',
            cell: (info) => (
                <div className="subject-cell" title={info.getValue()}>
                    {info.getValue() || 'No subject'}
                </div>
            ),
        },
        {
            header: 'Platform',
            accessorFn: (row) => getEnumLabel(notificationEnums?.platforms, row.notificationPlatform),
            id: 'platform',
            cell: (info) => {
                const platform = info.getValue();
                const platformConfig = {
                    'EMAIL': { color: '#11315A', icon: '✉️' },
                    'SMS': { color: '#11315A', icon: '📱' },
                    'BOTH': { color: '#11315A', icon: '📨' }
                };
                const config = platformConfig[platform] || { color: '#6c757d', icon: '📋' };

                return (
                    <div className="platform-badge" style={{ background: config.color }}>
                        <span className="platform-icon">{config.icon}</span>
                        {platform || 'N/A'}
                    </div>
                );
            },
        },
        {
            header: 'Type',
            accessorFn: (row) => getEnumLabel(notificationEnums?.notificationTypes, row.notificationType),
            id: 'type',
            cell: (info) => (
                <div className="type-badge">
                    {info.getValue() || 'N/A'}
                </div>
            ),
        },
        {
            header: 'Status',
            accessorFn: (row) => row.activeNotification,
            id: 'status',
            cell: (info) => {
                const val = info.getValue();
                return (
                    <div className={`status-badge ${val === 'YES' ? 'active' : 'inactive'}`}>
                        {val === 'YES' ? 'Active' : 'Inactive'}
                    </div>
                );
            },
        },
        {
            header: 'Actions',
            id: 'actions',
            cell: ({ row }) => (
                <div className="action-buttons">
                    <button
                        onClick={() => handleView(row.original)}
                        title="View"
                        className="action-btn view-btn"
                    >
                        <span className="btn-icon">👁</span>
                    </button>
                    <button
                        onClick={() => handleEdit(row.original)}
                        title="Edit"
                        className="action-btn edit-btn"
                    >
                        <span className="btn-icon">✏</span>
                    </button>
                    <button
                        onClick={() => handleDelete(row.original)}
                        title="Delete"
                        className="action-btn delete-btn"
                    >
                        <span className="btn-icon">🗑</span>
                    </button>
                </div>
            ),
        }
    ], [notificationEnums, handleDelete]);

    return (
        <div className="notification-templates-container">
            {/* Header Section */}
            <div className="header-section">
                <div className="title-section">
                    <h1 className="page-title">Notification Templates</h1>
                    <p className="page-subtitle">Create and manage email & SMS notification templates</p>
                </div>
                <button onClick={handleNewTemplate} className="new-template-btn">
                    <span className="btn-icon">+</span>
                    New Template
                </button>
            </div>

            {/* Templates Table */}
            <div className="templates-table-container">
                <DataTable
                    columns={columns}
                    data={templates}
                    loading={loading || externalLoading}
                    emptyMessage="No notification templates found. Click 'New Template' to create one."
                    pageSize={10}
                    onRowDoubleClick={(row) => handleView(row.original)}
                />
            </div>

            {/* Modal */}
            {showModal && (
                <TemplateModal
                    modalTitle={modalTitle}
                    formData={formData}
                    errors={errors}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    handleInputChange={handleInputChange}
                    handleRichTextChange={handleRichTextChange}
                    handleTextareaChange={handleTextareaChange}
                    handleCheckboxChange={handleCheckboxChange}
                    handleCloseModal={handleCloseModal}
                    handleSave={handleSave}
                    saving={saving}
                    readOnly={modalTitle === 'View Notification Template'}
                    showEmailFields={showEmailFields}
                    showEmailEditor={showEmailEditor}
                    showSmsEditor={showSmsEditor}
                    notificationEnums={notificationEnums}
                    smtpSetups={smtpSetups}
                    reservedWords={reservedWords}
                />
            )}

            {/* Styles */}
            <style jsx>{`
                .notification-templates-container {
                    padding: 20px;
                    background: #f8f9fa;
                    min-height: 100vh;
                }

                .header-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 24px;
                    gap: 16px;
                }

                .title-section {
                    flex: 1;
                }

                .page-title {
                    margin: 0;
                    font-size: 24px;
                    font-weight: 600;
                    color: #11315A;
                }

                .page-subtitle {
                    margin: 4px 0 0 0;
                    color: #666;
                    font-size: 14px;
                }

                .new-template-btn {
                    background: #11315A;
                    color: white;
                    border: none;
                    padding: 8px 16px;
                    border-radius: 4px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-weight: 500;
                    font-size: 14px;
                    transition: background 0.2s ease;
                    white-space: nowrap;
                }

                .new-template-btn:hover {
                    background: #1a4a8a;
                }

                .btn-icon {
                    font-size: 14px;
                }

                .templates-table-container {
                    background: white;
                    border-radius: 6px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                }

                .subject-cell {
                    max-width: 300px;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    font-weight: 500;
                    color: #333;
                }

                .platform-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 11px;
                    font-weight: 600;
                    color: white;
                }

                .platform-icon {
                    font-size: 12px;
                }

                .type-badge {
                    display: inline-block;
                    padding: 4px 8px;
                    border-radius: 4px;
                    background: #f0f0f0;
                    color: #555;
                    font-size: 11px;
                    font-weight: 500;
                }

                .status-badge {
                    display: inline-block;
                    padding: 4px 8px;
                    border-radius: 4px;
                    font-size: 11px;
                    font-weight: 600;
                    color: white;
                }

                .status-badge.active {
                    background: #10b981;
                }

                .status-badge.inactive {
                    background: #ef4444;
                }

                .action-buttons {
                    display: flex;
                    gap: 4px;
                }

                .action-btn {
                    width: 30px;
                    height: 30px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                }

                .action-btn:hover {
                    opacity: 0.9;
                }

                .view-btn {
                    background: #0ea5e9;
                    color: white;
                }

                .edit-btn {
                    background: #11315A;
                    color: white;
                }

                .delete-btn {
                    background: #ef4444;
                    color: white;
                }

                /* Modal Styles */
                .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }

                .modal-container {
                    background: white;
                    border-radius: 8px;
                    width: 90%;
                    max-width: 900px;
                    max-height: 90vh;
                    overflow: hidden;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                }

                .modal-header {
                    padding: 16px 24px;
                    border-bottom: 1px solid #ddd;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    background: #11315A;
                    color: white;
                }

                .modal-title-section {
                    flex: 1;
                }

                .modal-title {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 600;
                }

                .template-id {
                    font-size: 11px;
                    opacity: 0.8;
                    margin-top: 2px;
                    display: block;
                    font-family: monospace;
                }

                .close-modal-btn {
                    background: none;
                    border: none;
                    color: white;
                    font-size: 24px;
                    cursor: pointer;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background 0.2s ease;
                }

                .close-modal-btn:hover {
                    background: rgba(255, 255, 255, 0.1);
                }

                .modal-tabs {
                    display: flex;
                    padding: 0 24px;
                    background: #f5f5f5;
                    border-bottom: 1px solid #ddd;
                }

                .tab-btn {
                    padding: 12px 20px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    font-size: 14px;
                    font-weight: 500;
                    color: #666;
                    border-bottom: 2px solid transparent;
                    transition: all 0.2s ease;
                }

                .tab-btn:hover {
                    color: #11315A;
                }

                .tab-btn.active {
                    color: #11315A;
                    border-bottom-color: #11315A;
                }

                .tab-icon {
                    font-size: 14px;
                }

                .modal-content {
                    padding: 24px;
                    max-height: 60vh;
                    overflow-y: auto;
                }

                .form-section {
                    margin-bottom: 24px;
                }

                .section-header {
                    margin-bottom: 16px;
                }

                .section-title {
                    font-size: 16px;
                    font-weight: 600;
                    color: #11315A;
                    margin: 0 0 4px 0;
                }

                .section-subtitle {
                    font-size: 13px;
                    color: #666;
                    margin: 0;
                }

                .section-divider {
                    height: 2px;
                    background: #11315A;
                    width: 40px;
                    border-radius: 1px;
                }

                .form-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
                    gap: 16px;
                }

                .form-group {
                    margin-bottom: 16px;
                }

                .form-group.full-width {
                    grid-column: 1 / -1;
                }

                .form-label {
                    display: block;
                    margin-bottom: 6px;
                    font-weight: 500;
                    color: #444;
                    font-size: 13px;
                }

                .required {
                    color: #ef4444;
                }

                .select-wrapper {
                    position: relative;
                }

                .form-select {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 14px;
                    color: #333;
                    background: white;
                    cursor: pointer;
                    appearance: none;
                    transition: border-color 0.2s ease;
                }

                .form-select:focus {
                    outline: none;
                    border-color: #11315A;
                }

                .form-select.error {
                    border-color: #ef4444;
                }

                .select-arrow {
                    position: absolute;
                    right: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    pointer-events: none;
                    color: #666;
                    font-size: 10px;
                }

                .form-input {
                    width: 100%;
                    padding: 8px 12px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 14px;
                    color: #333;
                    background: white;
                    transition: border-color 0.2s ease;
                }

                .form-input:focus {
                    outline: none;
                    border-color: #11315A;
                }

                .form-input.error {
                    border-color: #ef4444;
                }

                .error-message {
                    display: block;
                    margin-top: 4px;
                    color: #ef4444;
                    font-size: 12px;
                }

                .sms-editor-container {
                    position: relative;
                }

                .sms-textarea {
                    width: 100%;
                    padding: 12px;
                    border: 1px solid #ddd;
                    border-radius: 4px;
                    font-size: 14px;
                    color: #333;
                    background: white;
                    resize: vertical;
                    min-height: 100px;
                    transition: border-color 0.2s ease;
                    font-family: inherit;
                }

                .sms-textarea:focus {
                    outline: none;
                    border-color: #11315A;
                }

                .sms-textarea.error {
                    border-color: #ef4444;
                }

                .sms-counter {
                    position: absolute;
                    bottom: 8px;
                    right: 8px;
                    background: rgba(255, 255, 255, 0.9);
                    padding: 2px 6px;
                    border-radius: 3px;
                    font-size: 11px;
                    color: #666;
                }

                .char-count {
                    font-weight: 600;
                }

                .char-count.warning {
                    color: #f59e0b;
                }

                .char-count.danger {
                    color: #ef4444;
                }

                .modal-footer {
                    padding: 16px 24px;
                    border-top: 1px solid #ddd;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    background: #f5f5f5;
                }

                .status-toggle {
                    display: flex;
                    align-items: center;
                }

                .toggle-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                }

                .toggle-input {
                    display: none;
                }

                .toggle-slider {
                    width: 40px;
                    height: 20px;
                    background: #ddd;
                    border-radius: 20px;
                    position: relative;
                    transition: background-color 0.2s ease;
                }

                .toggle-slider:before {
                    content: '';
                    position: absolute;
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    background: white;
                    top: 2px;
                    left: 2px;
                    transition: transform 0.2s ease;
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
                }

                .toggle-input:checked + .toggle-slider {
                    background: #10b981;
                }

                .toggle-input:checked + .toggle-slider:before {
                    transform: translateX(20px);
                }

                .toggle-text {
                    font-weight: 500;
                    color: #444;
                    font-size: 13px;
                }

                .footer-actions {
                    display: flex;
                    gap: 8px;
                }

                .save-btn {
                    background: #11315A;
                    color: white;
                    border: none;
                    padding: 8px 20px;
                    border-radius: 4px;
                    cursor: pointer;
                    font-weight: 500;
                    font-size: 14px;
                    transition: background 0.2s ease;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }

                .save-btn:hover:not(:disabled) {
                    background: #1a4a8a;
                }

                .save-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .cancel-btn {
                    background: white;
                    color: #666;
                    border: 1px solid #ddd;
                    padding: 8px 20px;
                    border-radius: 4px;
                    cursor: pointer;
                    font-weight: 500;
                    font-size: 14px;
                    transition: all 0.2s ease;
                }

                .cancel-btn:hover:not(:disabled) {
                    background: #f5f5f5;
                    border-color: #ccc;
                }

                .cancel-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .spinner {
                    width: 14px;
                    height: 14px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top-color: white;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    to {
                        transform: rotate(360deg);
                    }
                }

                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .header-section {
                        flex-direction: column;
                        gap: 12px;
                    }
                    
                    .new-template-btn {
                        width: 100%;
                        justify-content: center;
                    }
                    
                    .modal-container {
                        width: 95%;
                        margin: 10px;
                    }
                    
                    .modal-header {
                        padding: 12px 16px;
                    }
                    
                    .modal-content {
                        padding: 16px;
                    }
                    
                    .modal-footer {
                        padding: 12px 16px;
                        flex-direction: column;
                        gap: 12px;
                    }
                    
                    .footer-actions {
                        width: 100%;
                        justify-content: flex-end;
                    }
                }

                @media (max-width: 480px) {
                    .notification-templates-container {
                        padding: 12px;
                    }
                    
                    .page-title {
                        font-size: 20px;
                    }
                    
                    .form-grid {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }
                    
                    .modal-tabs {
                        padding: 0 12px;
                    }
                    
                    .tab-btn {
                        padding: 8px 12px;
                        font-size: 13px;
                    }
                }
            `}</style>
        </div>
    );
};

export default NotificationTemplatesView;